import { ECustomCell, type ITableColumn } from "@/models/table.model";
import type { Node } from "@/swagger-models/cluster-service-client";
import { type INodePoolTable } from "@/table-models/node-pool.table-model";
export const nodeColumnName = {
  name: "name",
  status: "status",
  nodePool: "nodePool",
  cluster: "cluster",
  nvLinkDomainUid: "nvLinkDomainUid",
  nvLinkCliqueId: "nvLinkCliqueId",
  gpuType: "gpuType",
  readyTotalGPU: "readyTotalGPU",
  freeGpu: "freeGpu",
  readyTotalGpuMemory: "readyTotalGpuMemory",
  allocatedGpu: "allocatedGpu",
  allocatedGpuMemory: "allocatedGpuMemory",
  readyTotalCpuCores: "readyTotalCpuCores",
  readyTotalCpuMemory: "readyTotalCpuMemory",
  allocatedCpuCores: "allocatedCpuCores",
  allocatedCpuMemory: "allocatedCpuMemory",
};

enum ENodeColumnLabel {
  Name = "Name",
  Status = "Status",
  NodePool = "Node Pool",
  Cluster = "Cluster",
  NvLinkDomainUid = "MNNVL Domain UID",
  NvLinkCliqueId = "MNNVL domain clique ID",
  GpuType = "GPU Type",
  ReadyTotalGPU = "Ready / total GPU devices",
  FreeGpu = "Free GPU devices",
  ReadyTotalGpuMemory = "Ready / total GPU memory",
  AllocatedGpu = "Allocated GPU",
  AllocatedGpuMemory = "Allocated GPU memory",
  ReadyTotalCpuCores = "Ready / total CPU (Cores)",
  ReadyTotalCpuMemory = "Ready / total CPU memory",
  AllocatedCpuCores = "Allocated CPU (Cores)",
  AllocatedCpuMemory = "Allocated CPU memory",
}

export const allNodeColumnsMap: Record<keyof typeof nodeColumnName, ITableColumn> = {
  name: {
    name: nodeColumnName.name,
    label: ENodeColumnLabel.Name,
    field: (node: Node): string => node.name,
    sortable: true,
    align: "left",
  },
  status: {
    name: nodeColumnName.status,
    label: ENodeColumnLabel.Status,
    field: (node: Node): string => node.status,
    sortable: true,
    align: "left",
  },
  nodePool: {
    name: nodeColumnName.nodePool,
    label: "Node Pool",
    field: (node: Node): string => node.nodePool,
    sortable: true,
    align: "left",
  },
  cluster: {
    name: nodeColumnName.cluster,
    label: ENodeColumnLabel.Cluster,
    field: (nodePool: INodePoolTable): string => nodePool.clusterId,
    sortable: false,
    align: "left",
    customCell: ECustomCell.CLUSTER_ID_TO_NAME_COL,
    hideFilter: true,
  },
  nvLinkDomainUid: {
    name: nodeColumnName.nvLinkDomainUid,
    label: ENodeColumnLabel.NvLinkDomainUid,
    field: (node: Node): string => node.nvLinkDomainUid || "-",
    sortable: true,
    align: "left",
  },
  nvLinkCliqueId: {
    name: nodeColumnName.nvLinkCliqueId,
    label: ENodeColumnLabel.NvLinkCliqueId,
    field: (node: Node): string => node.nvLinkCliqueId || "-",
    sortable: true,
    align: "left",
  },
  gpuType: {
    name: nodeColumnName.gpuType,
    label: ENodeColumnLabel.GpuType,
    field: (node: Node): string => node.gpuInfo?.gpuType || "-",
    sortable: true,
    align: "left",
  },
  readyTotalGPU: {
    name: nodeColumnName.readyTotalGPU,
    label: ENodeColumnLabel.ReadyTotalGPU,
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
  freeGpu: {
    name: nodeColumnName.freeGpu,
    label: ENodeColumnLabel.FreeGpu,
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
  readyTotalGpuMemory: {
    name: nodeColumnName.readyTotalGpuMemory,
    label: ENodeColumnLabel.ReadyTotalGpuMemory,
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
  allocatedGpu: {
    name: nodeColumnName.allocatedGpu,
    label: ENodeColumnLabel.AllocatedGpu,
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
  allocatedGpuMemory: {
    name: nodeColumnName.allocatedGpuMemory,
    label: ENodeColumnLabel.AllocatedGpuMemory,
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
  readyTotalCpuCores: {
    name: nodeColumnName.readyTotalCpuCores,
    label: ENodeColumnLabel.ReadyTotalCpuCores,
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
  readyTotalCpuMemory: {
    name: nodeColumnName.readyTotalCpuMemory,
    label: ENodeColumnLabel.ReadyTotalCpuMemory,
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
  allocatedCpuCores: {
    name: nodeColumnName.allocatedCpuCores,
    label: ENodeColumnLabel.AllocatedCpuCores,
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
  allocatedCpuMemory: {
    name: nodeColumnName.allocatedCpuMemory,
    label: "Allocated CPU memory",
    field: (): string => "-",
    sortable: true,
    align: "left",
  },
};

export const nodeIndexColumns: ITableColumn[] = [
  { ...allNodeColumnsMap.name, display: true, mandatory: true },
  {
    ...allNodeColumnsMap.status,
    display: true,
  },
  {
    ...allNodeColumnsMap.nodePool,
    display: true,
  },
  {
    ...allNodeColumnsMap.cluster,
    display: true,
  },
  {
    ...allNodeColumnsMap.nvLinkDomainUid,
    display: true,
  },
  {
    ...allNodeColumnsMap.nvLinkCliqueId,
    display: true,
  },
  {
    ...allNodeColumnsMap.gpuType,
    display: true,
  },
  {
    ...allNodeColumnsMap.freeGpu,
    display: true,
  },
  {
    ...allNodeColumnsMap.readyTotalGpuMemory,
    display: false,
  },
  {
    ...allNodeColumnsMap.allocatedGpu,
    display: false,
  },
  {
    ...allNodeColumnsMap.allocatedGpuMemory,
    display: false,
  },
  {
    ...allNodeColumnsMap.readyTotalCpuMemory,
    display: false,
  },
  {
    ...allNodeColumnsMap.allocatedCpuMemory,
    display: false,
  },
];
